import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, useSearchParams, Navigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { ThemeProvider, createTheme, CssBaseline, Paper, Container, Box, Typography, Badge, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton, Fab, Tooltip, List, ListItem, ListItemText, CircularProgress, TextField, FormControl, InputLabel, Select, MenuItem, Alert, Chip } from '@mui/material';
import api from './api/axios';
import MagicLoginPage from './components/MagicLoginPage'; // Adjust path as needed
// --- Page & Component Imports ---
import Header from './components/Header';
import Footer from './components/Footer'; // <-- FOOTER IMPORT
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CompleteRegistrationPage from './pages/CompleteRegistrationPage';
import CatalogPage from './pages/CatalogPage';
import BrowsePage from './pages/BrowsePage';
import CategoryItemsPage from './pages/CategoryItemsPage';
import OrderPage from './pages/OrderPage';
import StatementsPage from './pages/StatementsPage';
import CartPage from './pages/CartPage';
import About from './pages/About';
import Care from './pages/CustomerCarePage';
import Stepper, { Step } from './components/Stepper';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
// --- START OF EDIT: Import new mobile pages ---
import MobileNavPage from './pages/MobileNavPage';
import MobileAiPopupPage from './pages/MobileAiPopupPage';
import TabletDashboard from './pages/TabletDashboard';
import InstallPrompt from './components/InstallPrompt';
import Link from '@mui/material/Link'; // Ensure Link is imported if used, otherwise simple imports
import AudioWaveform from './components/AudioWaveform'; // Import Waveform
import WhyUsPage from './pages/WhyUsPage';
import FaqPage from './pages/FaqPage';
// --- END OF EDIT ---
import ParentDashboardPage from './pages/ParentDashboardPage';
import { PageLoadingProvider, usePageLoading } from './contexts/PageLoadingContext';


// --- Icon Imports for Global Components ---
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SendIcon from '@mui/icons-material/Send';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import PhoneIcon from '@mui/icons-material/Phone';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'; // <-- START OF EDIT: Added Icon
// --- NEW: ICONS FOR VOICE INPUT ---
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ImageIcon from '@mui/icons-material/Image';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

const WavyArrow = () => (
  <svg
    width="90"
    height="120"
    viewBox="0 0 100 100"
    style={{
      position: 'absolute',
      bottom: 25,
      right: 95,
      transform: 'rotate(70deg) scaleX(-1)', // Flipped and rotated to point correctly
      zIndex: 1400,
      pointerEvents: 'none'
    }}
  >
    <defs>
      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#ff9800" />
      </marker>
    </defs>
    <path
      d="M 80,85 C 60,100 50,60 40,40 C 30,20 20,40 10,30"
      stroke="#ff9800"
      strokeWidth="4"
      fill="none"
      strokeLinecap="round"
      markerEnd="url(#arrowhead)"
    />
  </svg>
);
// Theme remains the same
const theme = createTheme({
  palette: {
    primary: {
      main: '#75a84b', // Your new primary color
      contrastText: '#ffffff',
    },
    secondary: { main: '#e9c46a' },
    background: { default: '#f4f6f8' },
    ultraLight: { main: '#75a84b' },
  },
  typography: { fontFamily: 'Roboto, sans-serif', h4: { fontWeight: 700 }, color: 'white' }
});

const SPECIAL_REQUEST_DISCLOSURE = "Matax will do its best to fulfill special request items. If we're unable to, we'll let you know once your order has been processed.";

// Main logic component
function AppContent() {
  const [scale, setScale] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const { isPageLoading } = usePageLoading();
  const editingOrderId = location.state?.editingOrderId;
  const [isMobile, setIsMobile] = useState(false);
  const [showMicTutorial, setShowMicTutorial] = useState(false);

  // --- GLOBAL STATE LIFTED FROM CATALOGPAGE ---
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem('accessToken'));
  const [orderItems, setOrderItems] = useState({});
  const [products, setProducts] = useState([]); // Product list is now global
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // --- AI CHAT STATE (LIFTED & UPDATED) ---
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [chatSuggestions, setChatSuggestions] = useState([]);
  const [loadingMessage, setLoadingMessage] = useState('Looking into your request...');
  const chatEndRef = useRef(null);
  // --- NEW: State and Refs for voice recording ---
  const [isRecording, setIsRecording] = useState(false);
  const [audioStream, setAudioStream] = useState(null); // Add stream state
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  // --- NEW: State and Refs for Image Upload ---
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // --- START OF EDIT: State for AI notification lifted from Header ---
  const [showAiNotification, setShowAiNotification] = useState(false);
  // --- END OF EDIT ---

  // --- START OF EDIT: New State for Cart Conflict Resolution ---
  const [cartConflictOpen, setCartConflictOpen] = useState(false);
  const [pendingAiProposal, setPendingAiProposal] = useState(null);
  const [pendingAutoAction, setPendingAutoAction] = useState(null); // 'mic' or 'camera'
  // --- END OF EDIT ---

  const [isSpeedDialOpen, setSpeedDialOpen] = useState(false);
  const [stepperOpen, setStepperOpen] = useState(false);
  const [issue, setIssue] = useState('');
  const [orderId, setOrderId] = useState('');
  const [details, setDetails] = useState('');

  // --- START OF EDIT: Search State Lifted (Synced with URL) ---
  // location is already defined above
  const searchParams = new URLSearchParams(location.search);
  const searchTerm = searchParams.get('search') || '';

  const setSearchTerm = useCallback((term) => {
    const currentParams = new URLSearchParams(location.search);
    const isNewSearch = !currentParams.get('search') && !!term;

    if (term) currentParams.set('search', term);
    else currentParams.delete('search');

    // If there's a search term and we're not on browse, go to browse
    const targetPath = (term && location.pathname !== '/browse') ? '/browse' : location.pathname;

    navigate(
      { pathname: targetPath, search: currentParams.toString(), hash: location.hash },
      { replace: !isNewSearch }
    );
  }, [location.search, location.hash, location.pathname, navigate]);
  // --- END OF EDIT ---

  // --- START OF EDIT: Scroll to Top state and ref ---
  const [showScrollTop, setShowScrollTop] = useState(false);
  const scrollContainerRef = useRef(null);
  // --- END OF EDIT ---

  // --- START OF EDIT: Incomplete Profile Popup ---
  const [incompleteProfileOpen, setIncompleteProfileOpen] = useState(false);

  // --- START OF EDIT: Landing Redirect Logic ---
  // Immediate redirect check - runs on mount and location change
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const isParent = localStorage.getItem('isParent');
    // Only redirect if NOT an intentional visit (via Home button)
    if (location.pathname === '/' && token && !location.state?.intentional) {
      if (isParent === 'true') {
        navigate('/parent-dashboard', { replace: true });
      } else {
        navigate('/browse', { replace: true });
      }
    }
  }, [location.pathname, navigate, location.state]);
  // --- END OF EDIT ---

  useEffect(() => {
    const checkProfileStatus = () => {
      const incomplete = localStorage.getItem('profileIncomplete');
      // Don't show if already on the completion page or login page
      if (incomplete && location.pathname !== '/profile' && location.pathname !== '/login') {
        const lastShown = localStorage.getItem('lastProfilePromptShown');
        const now = Date.now();
        const eightHours = 8 * 60 * 60 * 1000;
        const lastShownTime = parseInt(lastShown);

        if (!lastShown || isNaN(lastShownTime) || (now - lastShownTime > eightHours)) {
          setIncompleteProfileOpen(true);
          localStorage.setItem('lastProfilePromptShown', now.toString());
        }
      } else {
        setIncompleteProfileOpen(false);
      }
    };
    checkProfileStatus();

    // We also listen for storage events in case it changes
    const handleStorageChange = () => checkProfileStatus();
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [location.pathname]);
  // --- END OF EDIT ---

  const isOnCatalogPage = location.pathname === '/catalog';
  const showCareButton = location.pathname === '/' || location.pathname === '/about' || location.pathname === '/care';
  const markdownStyles = {
    '& .markdown-content p': { margin: '0 0 8px 0' },
    '& .markdown-content h1, & .markdown-content h2, & .markdown-content h3': { marginTop: '10px', marginBottom: '4px', lineHeight: '1.2' },
    '& .markdown-content ul, & .markdown-content ol': { paddingLeft: '20px', margin: '0 0 8px 0' },
    '& .markdown-content a': { color: 'primary.main', textDecoration: 'underline' },
    '& .markdown-content pre': { backgroundColor: '#f0f0f0', padding: '8px', borderRadius: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' },
    '& .markdown-content code': { fontFamily: 'monospace', fontSize: '0.9em' },
    '& .markdown-content blockquote': { borderLeft: '3px solid #ccc', paddingLeft: '10px', margin: '8px 0', fontStyle: 'italic' },
  };

  // Track Website Visits
  useEffect(() => {
    const trackVisit = async () => {
      try {
        // Just fire and forget, backend will handle user extraction from token (if attached by axios interceptor)
        await api.post('/track/visit', {});
      } catch (e) {
        console.error("Failed to track visit:", e);
      }
    };
    trackVisit();
  }, []);

  // --- START OF EDIT: AI notification timer logic moved here ---
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('micTutorialShown');
    if (!hasSeenTutorial) {
      setShowMicTutorial(true);
    }
  }, []);

  // Dynamic loading messages that rotate every 5 seconds
  useEffect(() => {
    if (!isAiThinking) return;

    const loadingMessages = [
      'Looking into your request...',
      'Searching our fresh inventory...',
      'Finding the best products for you...',
      'Checking product availability...',
      'Analyzing your order history...',
      'Preparing your personalized recommendations...',
      'Crunching the numbers...',
      'Almost there...',
      'Just a moment more...'
    ];

    // Set initial random message
    const initialIndex = Math.floor(Math.random() * loadingMessages.length);
    setLoadingMessage(loadingMessages[initialIndex]);

    // Rotate messages every 5 seconds
    const interval = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * loadingMessages.length);
      setLoadingMessage(loadingMessages[randomIndex]);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAiThinking]);
  // useEffect(() => {
  //   let timer;
  //   // Only show the notification popup on the home page.
  //   if (location.pathname === '/') {
  //     timer = setTimeout(() => {
  //       // Check session storage to ensure it only shows once per session
  //       if (!sessionStorage.getItem('aiPopupShown')) {
  //         // Mark that we've now shown the popup for this session
  //         sessionStorage.setItem('aiPopupShown', 'true');

  //         // Check if user is already on one of the modal pages
  //         if (location.pathname !== '/mobile-ai-popup' && location.pathname !== '/mobile-nav') {
  //           setShowAiNotification(true);
  //         }
  //       }
  //     }, 5000);
  //   } else {
  //     setShowAiNotification(false);
  //   }
  //   return () => clearTimeout(timer);
  // }, [location.pathname]);

  // This effect handles redirecting to the mobile AI page when the notification is triggered
  useEffect(() => {
    if (showAiNotification && isMobile && location.pathname !== '/mobile-ai-popup') {
      navigate('/mobile-ai-popup');
      setShowAiNotification(false); // Reset after navigation
    }
  }, [showAiNotification, isMobile, navigate, location.pathname]);
  // --- END OF EDIT ---

  useEffect(() => {
    const checkIsMobile = () => {
      const mobileBreakpoint = 900;
      setIsMobile(window.innerWidth < mobileBreakpoint);
    };

    checkIsMobile(); // Initial check
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []); // Runs once on mount to set up the listener.

  // --- START OF EDIT: Effect to handle auto-actions (Mic/Camera) when chat opens ---
  useEffect(() => {
    if (isChatOpen && pendingAutoAction) {
      // Small timeout to ensure DOM is ready and animation has started
      const timer = setTimeout(() => {
        if (pendingAutoAction === 'mic') {
          // Toggle recording ON if not already recording
          if (!isRecording) {
            handleToggleRecording();
          }
        } else if (pendingAutoAction === 'camera') {
          if (cameraInputRef.current) {
            cameraInputRef.current.click();
          }
        }
        setPendingAutoAction(null); // Clear the action
      }, 500); // 500ms delay to allow modal to open
      return () => clearTimeout(timer);
    }
  }, [isChatOpen, pendingAutoAction, isRecording]);
  // --- END OF EDIT ---

  // Effect 2: Reacts to changes in the `isMobile` state to set the scale.
  useEffect(() => {
    if (isMobile) {
      // If it's mobile, set scale to 1.
      setScale(1);
    } else {
      // If it's not mobile, use the device pixel ratio.
      const zoom = window.devicePixelRatio;
      setScale(zoom > 1.4 ? 0.87 : 1);
    }
  }, [isMobile]); // <-- This dependency is crucial. This effect re-runs whenever `isMobile` changes.

  // Global effect to fetch all necessary data
  const checkAuthAndFetchData = useCallback(async () => {
    setIsLoading(true);
    const token = localStorage.getItem('accessToken');
    const userIsLoggedIn = !!token;
    setIsLoggedIn(userIsLoggedIn);

    try {
      // --- OPTIMIZATION: Fetch products and cart data in parallel ---
      const productsPromise = api.get('/products');
      // If logged in, fetch cart. Otherwise, create a resolved promise with an empty cart structure.
      const cartPromise = userIsLoggedIn ? api.get('/cart') : Promise.resolve({ data: { items: [] } });

      // Await both promises to complete concurrently
      const [productsResponse, cartResponse] = await Promise.all([productsPromise, cartPromise]);

      // 1. Process and set products
      const fetchedProducts = productsResponse.data;


      setProducts(fetchedProducts);

      // 2. Process and set cart/order items
      if (userIsLoggedIn) {
        const fetchedCartItems = cartResponse.data.items || [];
        const initialOrderItems = fetchedCartItems.reduce((acc, cartItem) => {
          // Use a composite key: productId-mode
          const key = `${cartItem.product.id}-${cartItem.mode || 'piece'}`;
          acc[key] = { product: cartItem.product, quantity: cartItem.quantity, mode: cartItem.mode || 'piece' };
          return acc;
        }, {});
        setOrderItems(initialOrderItems);
      } else {
        setOrderItems({}); // Ensure cart is empty if not logged in
      }
    } catch (err) {
      console.error("Failed to fetch initial data:", err);
      if (err.response && err.response.status === 401) {
        localStorage.removeItem('accessToken');
        setIsLoggedIn(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuthAndFetchData();

    const handleStorageChange = () => checkAuthAndFetchData();
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkAuthAndFetchData]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // --- START OF EDIT: Scroll to Top listener ---
  useEffect(() => {
    const container = scrollContainerRef.current;

    const handleScroll = debounce(() => {
      if (container) {
        setShowScrollTop(container.scrollTop > 400);
      }
    }, 100);


    // Only attach listener if we are on the catalog page
    if (isOnCatalogPage && container) {
      container.addEventListener('scroll', handleScroll);

      // Initial check in case of page reload
      handleScroll();

      return () => {
        container.removeEventListener('scroll', handleScroll);
        handleScroll.cancel(); // Cancel any pending debounce calls
      };
    }
    // If we navigate away, hide the button
    setShowScrollTop(false);
  }, [isOnCatalogPage]); // Re-run when page changes
  // --- END OF EDIT ---

  // ------------------ PERFORMANCE: stable debounced backend save ------------------
  const isLoggedInRef = useRef(isLoggedIn);
  useEffect(() => { isLoggedInRef.current = isLoggedIn; }, [isLoggedIn]);

  const saveCartToBackendRef = useRef(null);
  useEffect(() => {
    // Create the debounced function once
    saveCartToBackendRef.current = debounce(async (updatedCart, deliveryDate) => {
      if (!isLoggedInRef.current) return;
      try {
        const payload = {
          items: Object.values(updatedCart).map(item => ({ productId: item.product.id, quantity: item.quantity, mode: item.mode }))
        };
        if (deliveryDate !== undefined) {
          payload.deliveryDate = deliveryDate;
        }
        await api.post('/cart', payload);
      } catch (err) { console.error("Failed to save cart:", err); }
    }, 800);

    return () => {
      if (saveCartToBackendRef.current && saveCartToBackendRef.current.cancel) {
        saveCartToBackendRef.current.cancel();
      }
    };
  }, []);

  // stable handlers to avoid re-creating functions each render
  // stable handlers to avoid re-creating functions each render
  const handleOpenChat = useCallback((initialQuery, autoStartMode) => {
    setChatMessages([{ sender: 'ai', type: 'text', data: 'Hello! I can help you place an order, find a page, or look up your recent orders. How can I assist?' }]);
    setIsChatOpen(true);
    setChatSuggestions(["Order my usuals", "Contact Support", "View Catalog"]);

    if (autoStartMode) {
      setPendingAutoAction(autoStartMode);
    }

    if (typeof initialQuery === 'string' && initialQuery.trim()) {
      setTimeout(() => {
        handleSendChatMessage(initialQuery);
      }, 100);
    }
  }, []); // handleSendChatMessage defined later uses chatMessages slice; it is fine

  const handleGuestAction = useCallback(() => setShowLoginPopup(true), []);
  const handleCloseLoginPopup = useCallback(() => setShowLoginPopup(false), []);
  const handleLoginRedirect = useCallback(() => { navigate('/login'); handleCloseLoginPopup(); }, [navigate, handleCloseLoginPopup]);
  const handleProceedToCart = useCallback(() => {
    navigate('/cart', { state: { editingOrderId } });
  }, [navigate, editingOrderId]);
  const handleNavigationClick = useCallback((path) => { navigate(path); handleCloseChat(); }, [navigate]);

  // --- START OF EDIT: Scroll to Top handler ---
  const handleScrollToTop = useCallback(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  // --- END OF EDIT ---

  // stable save function wrapper
  const saveCartToBackend = useCallback((updatedCart, deliveryDate) => {
    if (saveCartToBackendRef.current) saveCartToBackendRef.current(updatedCart, deliveryDate);
  }, []);

  const handleUpdateCart = useCallback((product, quantity, mode) => {
    const key = `${product.id}-${mode}`; // Create a unique key for the product-mode combination

    setOrderItems(prev => {
      const newOrderItems = { ...prev };
      const numericQuantity =
        (mode === 'weight' ||
          mode === 'lb' ||
          mode === 'pound' ||
          mode.toLowerCase().includes('case') ||
          mode.toLowerCase().includes('bag'))
          ? parseFloat(quantity)
          : parseInt(quantity, 10);

      if (numericQuantity > 0) {
        newOrderItems[key] = { product, quantity: numericQuantity, mode };
      } else {
        delete newOrderItems[key];
      }
      // call the stable debounced save
      saveCartToBackend(newOrderItems);
      return newOrderItems;
    });
  }, [saveCartToBackend]);

  // AI CHAT HANDLERS (LIFTED & UPDATED)
  const handleSendChatMessage = useCallback(async (messageOverride) => {
    const messageToSend = messageOverride || chatInput;
    if ((!messageToSend.trim() && !selectedImage) || isAiThinking) return;

    const newUserMessage = {
      sender: 'user',
      type: selectedImage ? 'mixed' : 'text',
      data: messageToSend,
      image: selectedImage ? URL.createObjectURL(selectedImage) : null
    };

    setChatMessages(prev => [...prev, newUserMessage]);
    setChatInput('');
    setChatSuggestions([]); // Clear suggestions when user sends a message
    setIsAiThinking(true);

    // Capture current image ref and clear UI
    const imageToSend = selectedImage;
    setSelectedImage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    try {
      // Get the JWT token
      const token = localStorage.getItem('accessToken');
      let body;
      let headers = {
        'Authorization': `Bearer ${token}`
      };

      if (imageToSend) {
        // Use FormData for image uploads
        const formData = new FormData();
        formData.append('message', messageToSend);
        formData.append('image', imageToSend);
        formData.append('history', JSON.stringify(chatMessages.slice(-10)));
        body = formData;
        // Content-Type is left undefined so browser sets boundary
      } else {
        // Use JSON for text-only
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({
          message: messageToSend,
          history: chatMessages.slice(-10),
        });
      }

      // Use fetch for SSE streaming
      const response = await fetch(`${api.defaults.baseURL}/chat`, {
        method: 'POST',
        headers: headers,
        body: body
      });

      // Check response status
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check if it's a streaming response
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/event-stream')) {
        // Handle streaming response
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let streamedText = '';
        let messageIndex = null;

        // Add initial empty AI message for streaming
        setChatMessages(prev => {
          messageIndex = prev.length;
          return [...prev, { sender: 'ai', type: 'text', data: '', isStreaming: true }];
        });

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));

                  if (data.type === 'text_chunk') {
                    streamedText += data.data;
                    // Update the message in real-time
                    setChatMessages(prev => {
                      const newMessages = [...prev];
                      if (messageIndex !== null && newMessages[messageIndex]) {
                        newMessages[messageIndex] = {
                          ...newMessages[messageIndex],
                          data: streamedText
                        };
                      }
                      return newMessages;
                    });
                  } else if (data.type === 'image_saved') {
                    const filename = data.data.filename;
                    setChatMessages(prev => {
                      const newMessages = [...prev];
                      for (let i = newMessages.length - 1; i >= 0; i--) {
                        if (newMessages[i].sender === 'user' && !newMessages[i].server_image_filename) {
                          newMessages[i] = { ...newMessages[i], server_image_filename: filename };
                          break;
                        }
                      }
                      return newMessages;
                    });
                  } else if (data.type === 'suggestions') {
                    setChatSuggestions(data.data);
                  } else if (data.type === 'done') {
                    // Mark streaming as complete
                    setChatMessages(prev => {
                      const newMessages = [...prev];
                      if (messageIndex !== null && newMessages[messageIndex]) {
                        newMessages[messageIndex] = {
                          ...newMessages[messageIndex],
                          isStreaming: false
                        };
                      }
                      return newMessages;
                    });
                  } else if (data.type === 'error') {
                    console.error('Streaming error:', data.data);
                  }
                } catch (e) {
                  console.error('Error parsing SSE data:', e);
                }
              }
            }
          }
        } catch (streamError) {
          console.error('Stream reading error:', streamError);
          // Clean up incomplete streaming message
          setChatMessages(prev => {
            const newMessages = [...prev];
            if (messageIndex !== null && newMessages[messageIndex] && !newMessages[messageIndex].data) {
              newMessages[messageIndex] = {
                sender: 'ai',
                type: 'text',
                data: 'Sorry, there was an error receiving the response.'
              };
            }
            return newMessages;
          });
        }
      } else {
        // Handle non-streaming response (function calls, etc.)
        const data = await response.json();
        setChatMessages(prev => [...prev, { sender: 'ai', ...data }]);
        if (data.suggestions) {
          setChatSuggestions(data.suggestions);
        }
        if (data.image_saved) {
          const filename = data.image_saved.filename;
          setChatMessages(prev => {
            const newMessages = [...prev];
            for (let i = newMessages.length - 1; i >= 0; i--) {
              if (newMessages[i].sender === 'user' && !newMessages[i].server_image_filename) {
                newMessages[i] = { ...newMessages[i], server_image_filename: filename };
                break;
              }
            }
            return newMessages;
          });
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, { sender: 'ai', type: 'text', data: error.response?.data?.msg || 'An error occurred.' }]);
    } finally {
      setIsAiThinking(false);
    }
  }, [chatInput, chatMessages, isAiThinking]);

  const handleCloseChat = useCallback(() => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
    }
    setIsChatOpen(false);
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [isRecording]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  const handlePaste = (event) => {
    const items = (event.clipboardData || event.originalEvent.clipboardData).items;
    for (let index in items) {
      const item = items[index];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        setSelectedImage(file);
        event.preventDefault();
        return;
      }
    }
  };

  const handleClearImage = () => {
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendVoiceMessage = useCallback(async (audioBlob) => {
    const userVoiceMessage = { sender: 'user', type: 'voice_prompt', data: '🎤 Voice input sent...' };
    setChatMessages(prev => [...prev, userVoiceMessage]);
    setIsAiThinking(true);

    const formData = new FormData();
    formData.append('audio', audioBlob, 'voice-order.webm');
    formData.append('history', JSON.stringify(chatMessages.slice(-10)));

    try {
      const token = localStorage.getItem('accessToken');

      // Use fetch instead of axios to handle streaming response
      const response = await fetch(`${api.defaults.baseURL}/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Note: Content-Type header is intentionally omitted so the browser sets the boundary for FormData
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check content type for streaming vs non-streaming
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('text/event-stream')) {
        // --- STREAMING LOGIC ---
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let streamedText = '';
        let messageIndex = null;

        // Add initial empty AI message for streaming
        setChatMessages(prev => {
          messageIndex = prev.length;
          return [...prev, { sender: 'ai', type: 'text', data: '', isStreaming: true }];
        });

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));

                  if (data.type === 'text_chunk') {
                    streamedText += data.data;
                    // Update the message in real-time
                    setChatMessages(prev => {
                      const newMessages = [...prev];
                      if (messageIndex !== null && newMessages[messageIndex]) {
                        newMessages[messageIndex] = {
                          ...newMessages[messageIndex],
                          data: streamedText
                        };
                      }
                      return newMessages;
                    });
                  } else if (data.type === 'done') {
                    // Mark streaming as complete
                    setChatMessages(prev => {
                      const newMessages = [...prev];
                      if (messageIndex !== null && newMessages[messageIndex]) {
                        newMessages[messageIndex] = {
                          ...newMessages[messageIndex],
                          isStreaming: false
                        };
                      }
                      return newMessages;
                    });
                  } else if (data.type === 'error') {
                    console.error('Streaming error:', data.data);
                  }
                } catch (e) {
                  console.error('Error parsing SSE data:', e);
                }
              }
            }
          }
        } catch (streamError) {
          console.error('Stream reading error:', streamError);
          setChatMessages(prev => {
            const newMessages = [...prev];
            if (messageIndex !== null && newMessages[messageIndex] && !newMessages[messageIndex].data) {
              newMessages[messageIndex] = {
                sender: 'ai',
                type: 'text',
                data: 'Sorry, there was an error receiving the voice response.'
              };
            }
            return newMessages;
          });
        }
      } else {
        // Handle non-streaming response (standard JSON)
        const data = await response.json();
        setChatMessages(prev => [...prev, { sender: 'ai', ...data }]);
      }
    } catch (error) {
      console.error('Voice Chat error:', error);
      setChatMessages(prev => [...prev, { sender: 'ai', type: 'text', data: error.message || 'An error occurred processing your voice input.' }]);
    } finally {
      setIsAiThinking(false);
    }
  }, [chatMessages]);

  const handleToggleRecording = useCallback(async () => {

    if (showMicTutorial) {
      localStorage.setItem('micTutorialShown', 'true');
      setShowMicTutorial(false);
    }
    if (isRecording) {
      mediaRecorderRef.current?.stop();
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];
        setAudioStream(stream); // Set stream for visualization

        mediaRecorder.addEventListener("dataavailable", event => {
          audioChunksRef.current.push(event.data);
        });

        mediaRecorder.addEventListener("stop", () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          handleSendVoiceMessage(audioBlob);
          stream.getTracks().forEach(track => track.stop());
          setIsRecording(false);
          setAudioStream(null); // Clear stream
        });

        mediaRecorder.start();
        setIsRecording(true);
      } catch (err) {
        console.error("Error accessing microphone:", err);
        alert("Could not access the microphone. Please check your browser permissions.");
      }
    }
  }, [isRecording, handleSendVoiceMessage, showMicTutorial]);

  // --- MODIFIED: Main Logic Execution for AI Cart Addition ---
  const executeAiCartAddition = useCallback(
    async (proposalData, clearFirst) => {
      if (!proposalData) return;
      const { items: proposedItems = [], delivery_date: proposedDate } = proposalData;
      const standardItems = [];
      const specialItems = [];

      proposedItems.forEach((item) => {
        if (item.is_special) {
          specialItems.push(item);
        } else {
          standardItems.push(item);
        }
      });

      const newOrderItems = clearFirst ? {} : { ...orderItems };
      let itemsAdded = 0;

      standardItems.forEach((item) => {
        const product = products.find(
          (p) =>
            (item.product_id && p.id === item.product_id) ||
            (!item.product_id &&
              p.name &&
              item.product_name &&
              p.name.toLowerCase() === item.product_name.toLowerCase())
        );
        if (!product) {
          return;
        }
        if (!product.modes || !product.modes[item.unit]) {
          return;
        }
        const key = `${product.id}-${item.unit}`;
        newOrderItems[key] = { product, quantity: item.quantity, mode: item.unit };
        itemsAdded++;
      });

      if (itemsAdded > 0 || clearFirst) {
        setOrderItems(newOrderItems);
        saveCartToBackend(newOrderItems, proposedDate);
      }

      let confirmationMessage = "";
      if (itemsAdded > 0) {
        confirmationMessage = `Great! I've ${clearFirst ? "cleared your cart and " : ""}added ${itemsAdded} item(s) to your cart.`;
        if (proposedDate) {
          confirmationMessage += ` I've also set the delivery date to ${proposedDate}.`;
        }
      } else if (!specialItems.length) {
        confirmationMessage = "I couldn't match those items to our catalog. Please double-check the product names.";
      }

      let specialMessage = "";
      if (specialItems.length) {
        try {
          await api.post("/cart", {
            special_items: specialItems.map((item) => ({
              name: item.product_name,
              quantity: item.quantity,
              mode: item.unit,
            })),
          });
          specialMessage = `I've also noted ${specialItems.length} special request item${specialItems.length > 1 ? "s" : ""}. ${SPECIAL_REQUEST_DISCLOSURE}`;
        } catch (error) {
          console.error("Failed to add special request items", error);
          specialMessage =
            "I couldn't add the special request items automatically. Please add them manually from the catalog special request button.";
        }
      }

      const responses = [];
      if (confirmationMessage) responses.push(confirmationMessage);
      if (specialMessage) responses.push(specialMessage);
      if (responses.length) {
        setChatMessages((prev) => [...prev, { sender: "ai", type: "text", data: responses.join("\n\n") }]);
      }

      // After adding items, navigate to cart
      setTimeout(() => {
        handleCloseChat();
        setCartConflictOpen(false);
        setPendingAiProposal(null);
        navigate('/cart', { state: { editingOrderId } });
      }, 500);
    },
    [orderItems, products, saveCartToBackend, handleCloseChat, navigate, editingOrderId]
  );


  // --- MODIFIED: Button Click Handler ---
  // This is what triggers when user clicks "Proceed to Cart" in chat
  const handleAddAiOrderToCart = useCallback(
    async (proposalData) => {
      try {
        // Check if cart has items
        if (Object.keys(orderItems).length > 0) {
          setPendingAiProposal(proposalData);
          setCartConflictOpen(true);
        } else {
          // Automatically add items to cart
          await executeAiCartAddition(proposalData, false);
          // Wait a moment for the cart to update, then navigate
          setTimeout(() => {
            handleCloseChat();
            navigate('/cart', { state: { editingOrderId } });
          }, 500);
        }
      } catch (error) {
        console.error("Failed to add AI items to cart", error);
      }
    },
    [orderItems, executeAiCartAddition, navigate, handleCloseChat, editingOrderId]
  );


  const handleDeleteProposalItem = useCallback(
    (msgIndex, itemIndex) => {
      setChatMessages((prev) => {
        const newMessages = [...prev];
        const msg = { ...newMessages[msgIndex] };
        if (msg && msg.type === 'order_proposal' && msg.data && msg.data.items) {
          const newItems = [...msg.data.items];
          newItems.splice(itemIndex, 1);
          msg.data = { ...msg.data, items: newItems };
          newMessages[msgIndex] = msg;
        }
        return newMessages;
      });
    },
    []
  );


  const handleSpeedDialOpen = () => setSpeedDialOpen(true);
  const handleSpeedDialClose = () => setSpeedDialOpen(false);
  const handleStepperOpen = () => setStepperOpen(true);
  const handleStepperClose = () => {
    setIssue('');
    setOrderId('');
    setDetails('');
    setStepperOpen(false);
  };
  const onStepperCompleted = async () => {
    try {
      const payload = {
        subject: issue,
        message: `${details}${orderId ? ` (Order ID: ${orderId})` : ''}`,
        name: localStorage.getItem('contactPerson') || 'Guest',
        email: localStorage.getItem('email') || 'guest@example.com',
        phone: ''
      };
      await api.post('/support', payload);
      alert('Your support ticket has been created successfully.');
    } catch (error) {
      console.error("Support ticket error:", error);
      alert('Failed to create support ticket. Please try again.');
    }
    handleStepperClose();
    navigate('/care');
  };
  const contactActions = [
    { icon: <PhoneIcon />, name: 'Contact Customer Care', action: handleStepperOpen },
  ];

  const totalUniqueItemsInOrder = useMemo(() => Object.values(orderItems).length, [orderItems]);

  // --- Persistent BrowsePage Scroll Management ---
  const browseScrollPos = useRef(0);
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const isLeavingBrowse = prevPath.current === '/browse' && location.pathname !== '/browse';
    const isEnteringBrowse = location.pathname === '/browse';

    if (isLeavingBrowse) {
      browseScrollPos.current = container.scrollTop;
    }

    if (isEnteringBrowse) {
      if (location.state?.resetScroll) {
        browseScrollPos.current = 0;
        if (container) container.scrollTop = 0;
      } else {
        requestAnimationFrame(() => {
          if (container) container.scrollTop = browseScrollPos.current;
        });
      }
    } else if (location.pathname !== prevPath.current) {
      container.scrollTop = 0;
    }

    prevPath.current = location.pathname;
  }, [location.pathname]);

  const handleConfirmCancellation = async (orderId) => {
    const optimisticCancelMessage = { sender: 'user', type: 'text', data: `Attempting to cancel order ${orderId.slice(-6)}...` };
    setChatMessages(prev => [...prev, optimisticCancelMessage]);
    setIsAiThinking(true);

    try {
      const response = await api.post(`/orders/${orderId}/cancel`);
      setChatMessages(prev => [...prev, { sender: 'ai', type: 'text', data: response.data.msg || 'Order successfully cancelled.' }]);
    } catch (error) {
      const errorMessage = error.response?.data?.msg || 'Failed to cancel the order.';
      setChatMessages(prev => [...prev, { sender: 'ai', type: 'text', data: `Error: ${errorMessage}` }]);
    } finally {
      setIsAiThinking(false);
    }
  };
  return (
    <>
      {/* --- START OF EDIT: Added ref to main scroll container --- */}
      <div id="scroll-container" ref={scrollContainerRef} style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: `${100 / scale}%`, height: `${100 / scale}vh`, position: 'fixed', top: 0, left: 0, overflow: 'auto' }}>
        {/* --- END OF EDIT --- */}
        <Box sx={{ position: 'sticky', top: 0, zIndex: 1100 }}>
          {/* --- START OF EDIT: Pass AI notification state to Header --- */}
          <Header
            onAskAiClick={isLoggedIn ? handleOpenChat : handleGuestAction}
            showAiNotification={showAiNotification}
            setShowAiNotification={setShowAiNotification}
            cartItemCount={totalUniqueItemsInOrder}
            products={products}
            orderItems={orderItems} // Passed for "In Cart" check
            searchTerm={searchTerm} // Passed for controlled input
            onSearchChange={setSearchTerm} // Passed for updating state
            onUpdateCart={handleUpdateCart}
            onGuestAction={handleGuestAction}
            isLoggedIn={isLoggedIn}
          />
          {/* --- END OF EDIT --- */}
        </Box>

        {/* Persistent BrowsePage */}
        <div style={{ display: location.pathname === '/browse' ? 'block' : 'none' }}>
          <BrowsePage
            products={products}
            isLoggedIn={isLoggedIn}
            orderItems={orderItems}
            searchTerm={searchTerm} // Passed for filtering
            onClearSearch={() => setSearchTerm('')}
            onUpdateCart={handleUpdateCart}
            onGuestAction={handleGuestAction}
            isLoading={isLoading}
            onAiClick={isLoggedIn ? handleOpenChat : handleGuestAction}
          />
        </div>

        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/profile" element={<CompleteRegistrationPage />} />
          <Route path="/category/:category" element={<CategoryItemsPage
            products={products}
            isLoggedIn={isLoggedIn}
            orderItems={orderItems}
            onUpdateCart={handleUpdateCart}
            onGuestAction={handleGuestAction}
          />} />

          <Route path="/catalog" element={<Navigate to="/browse" replace />} />
          <Route path="/order" element={<OrderPage />} />
          <Route path="/statements" element={<StatementsPage />} />

          <Route path="/magic-login/:token" element={<MagicLoginPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/about" element={<About />} />
          <Route path="/care" element={<Care onOpenSupportForm={handleStepperOpen} />} />

          {/* --- START OF EDIT: Add routes for the new mobile pages --- */}
          <Route path="/mobile-nav" element={<MobileNavPage isLoggedIn={isLoggedIn} cartItemCount={totalUniqueItemsInOrder} />} />
          <Route path="/mobile-ai-popup" element={<MobileAiPopupPage onAskAiClick={isLoggedIn ? handleOpenChat : handleGuestAction} />} />
          <Route path="/tablet" element={<TabletDashboard />} />
          <Route path="/why-us" element={<WhyUsPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/parent-dashboard" element={<ParentDashboardPage />} />
          {/* --- END OF EDIT --- */}

        </Routes>
        {!isLoading && !isPageLoading && !['/login', '/register'].includes(location.pathname) && <Footer />}
      </div>

      <InstallPrompt />

      {/* Global UI Components */}
      {isOnCatalogPage && (
        <>
          {/* --- START OF EDIT: Scroll to Top Button --- */}
          {showScrollTop && (
            <Tooltip title="Search">
              <Fab
                variant="extended"
                color="primary"
                onClick={handleScrollToTop}
                sx={{
                  position: 'fixed',
                  bottom: 150,
                  right: isMobile ? 2 : 24,
                  maxWidth: isMobile ? 130 : 300,
                  color: "secondary",
                  bgcolor: '#f97316',
                  '&:hover': { bgcolor: '#fb923c' },
                  zIndex: 1201,
                }}
              >
                <ArrowUpwardIcon sx={{ mr: 1, color: 'white' }} />
                Search
              </Fab>
            </Tooltip>
          )}

          {/* --- END OF EDIT --- */}

          <Tooltip title={isLoggedIn ? "AI Ordering Assistant" : "Log in to use AI Assistant"}>
            <Fab
              variant="extended"
              color="primary"
              onClick={isLoggedIn ? handleOpenChat : handleGuestAction}
              sx={{
                position: 'fixed',
                color: "white",
                bottom: 90,
                right: isMobile ? 2 : 24,
                maxWidth: isMobile ? 130 : 300, // optional: shrink button width too
                bgcolor: '#f97316',
                '&:hover': { bgcolor: '#fb923c' },
                zIndex: 1201,
              }}
            >
              <SmartToyIcon sx={{ mr: 1, color: 'white' }} />
              AI Order
            </Fab>

          </Tooltip>
          {isLoggedIn ? (
            <Paper elevation={8} sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, p: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', zIndex: 1200 }}>
              <Container maxWidth="xl"><Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: '600' }}>Your Order:<Badge badgeContent={totalUniqueItemsInOrder} color="primary" sx={{ ml: 1 }}><ShoppingCartIcon /></Badge></Typography>
                <Button variant="contained" size="large" disabled={totalUniqueItemsInOrder === 0} onClick={handleProceedToCart} sx={{ backgroundColor: '#75a84b' }}>Proceed to Cart</Button>
              </Box></Container>
            </Paper>
          ) : (
            <Paper elevation={8} sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, bgcolor: '#75a84b', color: 'primary.contrastText', zIndex: 1200 }}>
              <Container maxWidth="xl"><Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant={isMobile ? 'subtitle2' : 'h6'} component="h3" sx={{ fontWeight: 'bold' }}>
                    See something you like?
                  </Typography>
                  <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ opacity: 0.9 }}>
                    {isMobile ? 'Log in to add items to cart.' : 'Log in or create an account to add items to your cart.'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size={isMobile ? 'small' : 'large'}
                  onClick={() => navigate('/login')}
                  sx={{
                    minWidth: isMobile ? 120 : 180,
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'grey.200' },
                    py: isMobile ? 1 : 1
                  }}
                >
                  {isMobile ? 'Log In' : 'Log In / Sign Up'}
                </Button>
              </Box></Container>
            </Paper>
          )}
        </>
      )}

      {showCareButton && (
        <>
          <SpeedDial
            ariaLabel="Customer Care"
            sx={{ position: 'fixed', bottom: 32, right: 12 }}
            icon={<SpeedDialIcon icon={<SupportAgentIcon />} />}
            onClose={handleSpeedDialClose}
            onOpen={handleSpeedDialOpen}
            open={isSpeedDialOpen}
            FabProps={{ sx: { bgcolor: '#f97316', '&:hover': { bgcolor: '#fb923c' } } }}
          >
            {contactActions.map((action) => (
              <SpeedDialAction
                key={action.name}
                icon={action.icon}
                tooltipTitle={action.name}
                tooltipOpen
                onClick={() => { action.action(); handleSpeedDialClose(); }}
              />
            ))}
          </SpeedDial>
          <Dialog open={stepperOpen} onClose={handleStepperClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Customer Support Assistant
              <IconButton aria-label="close" onClick={handleStepperClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Stepper onFinalStepCompleted={onStepperCompleted} backButtonText="Previous" nextButtonText="Next">
                <Step>
                  <Typography variant="h5" gutterBottom>Help us understand your issue</Typography>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="issue-select-label">What is your query about?</InputLabel>
                    <Select labelId="issue-select-label" value={issue} label="What is your query about?" onChange={(e) => setIssue(e.target.value)}>
                      <MenuItem value="Billing">Billing Question</MenuItem>
                      <MenuItem value="Delivery">Delivery Issue</MenuItem>
                      <MenuItem value="Product Quality">Product Quality</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Step>
                <Step>
                  <Typography variant="h5" gutterBottom>Order Information</Typography>
                  <TextField fullWidth margin="normal" label="Order ID (if applicable)" value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="e.g., #12345" />
                </Step>
                <Step>
                  <Typography variant="h5" gutterBottom>Describe the issue</Typography>
                  <TextField fullWidth multiline rows={4} margin="normal" label="Please provide more details" value={details} onChange={(e) => setDetails(e.target.value)} placeholder="The more information you provide, the faster we can help." />
                </Step>
                <Step>
                  <Typography variant="h5" gutterBottom>Ready to Submit</Typography>
                  <Typography>We have all the information we need. Click 'Finish' to create a support ticket. You will be redirected to our main contact page.</Typography>
                </Step>
              </Stepper>
            </DialogContent>
          </Dialog>
        </>
      )}

      <Dialog open={showLoginPopup} onClose={handleCloseLoginPopup} PaperProps={{ sx: { borderRadius: 4, textAlign: 'center', p: { xs: 2, sm: 3 } } }}>
        <DialogTitle sx={{ p: 0, position: 'relative' }}><IconButton aria-label="close" onClick={handleCloseLoginPopup} sx={{ position: 'absolute', top: 8, right: 8 }}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent sx={{ mt: 1 }}><Box sx={{ mb: 2, color: 'primary.main' }}><LockOpenIcon sx={{ fontSize: 60 }} /></Box><Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>A TASTY NEXT STEP</Typography><DialogContentText>Login/Sign Up to Add items to your cart and order now.</DialogContentText></DialogContent>
        <DialogActions sx={{ justifyContent: 'center', flexDirection: 'column', gap: 1.5, px: 3, pb: 2, pt: 2 }}><Button onClick={handleLoginRedirect} variant="contained" fullWidth size="large">Login / Sign Up</Button><Button onClick={handleCloseLoginPopup} color="inherit" fullWidth>Continue Browsing</Button></DialogActions>
      </Dialog>

      {/* --- NEW DIALOG: Incomplete Registration Popup --- */}
      <Dialog open={incompleteProfileOpen} onClose={() => setIncompleteProfileOpen(false)} PaperProps={{ sx: { borderRadius: 4, textAlign: 'center', p: { xs: 2, sm: 3 } } }}>
        <DialogTitle sx={{ p: 0, color: '#d32f2f', fontWeight: 'bold' }}>
          Action Required
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            Complete Your Registration
          </Typography>
          <DialogContentText>
            Your account details are incomplete. Please update your profile to ensure seamless ordering and delivery.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', flexDirection: 'column', gap: 1.5, px: 3, pb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={() => {
              setIncompleteProfileOpen(false);
              navigate('/profile');
            }}
            sx={{ bgcolor: '#75a84b', color: 'white' }}
          >
            Go to My Profile
          </Button>
          <Button
            onClick={() => setIncompleteProfileOpen(false)}
            color="inherit"
            fullWidth
          >
            Remind Me Later
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- NEW DIALOG: Cart Conflict Resolution --- */}
      <Dialog open={cartConflictOpen} onClose={() => setCartConflictOpen(false)} PaperProps={{ sx: { borderRadius: 2, p: 1, zIndex: 1500 } }}>
        <DialogTitle sx={{ fontWeight: 'bold', color: '#75a84b' }}>Items already in cart</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You already have items in your cart. Would you like to add these new items on top of your existing order, or replace your cart entirely?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={async () => {
              if (!pendingAiProposal) return;
              try {
                await executeAiCartAddition(pendingAiProposal, false);
              } catch (error) {
                console.error("Failed to merge AI items", error);
              }
            }}
            startIcon={<AddCircleIcon />}
            sx={{ bgcolor: '#75a84b' }}
          >
            Add to Existing Order
          </Button>
          <Button
            variant="outlined"
            fullWidth
            color="error"
            onClick={async () => {
              if (!pendingAiProposal) return;
              try {
                await executeAiCartAddition(pendingAiProposal, true);
              } catch (error) {
                console.error("Failed to replace cart with AI items", error);
              }
            }}
            startIcon={<DeleteForeverIcon />}
          >
            Clear Cart & Add New Items
          </Button>
          <Button onClick={() => setCartConflictOpen(false)} color="inherit" size="small">Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isChatOpen} onClose={handleCloseChat} {...(isMobile ? { fullScreen: true, PaperProps: { sx: { zIndex: 1300 } } } : { fullWidth: true, maxWidth: "sm", PaperProps: { sx: { height: '80vh', borderRadius: 4, zIndex: 1300 } } })}>
        <DialogTitle sx={{ bgcolor: 'grey.100', display: 'flex', alignItems: 'center', gap: 1.5 }}><SmartToyIcon sx={{ color: '#75a84b' }} /><Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>AI Assistant</Typography><IconButton onClick={handleCloseChat}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: 'grey.50' }}>
          <List sx={{ height: '100%', overflowY: 'auto', p: 2 }}>
            {chatMessages.map((msg, index) => (<ListItem key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'ai' ? 'flex-start' : 'flex-end', mb: 1 }}><Paper elevation={1} sx={{ p: 1.5, maxWidth: '85%', bgcolor: msg.sender === 'ai' ? 'white' : '#e6f2d9', borderRadius: msg.sender === 'ai' ? '20px 20px 20px 5px' : '20px 20px 5px 20px' }}>
              {(msg.type === 'text' || msg.type === 'mixed') && (
                <Box className="markdown-content">
                  {msg.image && (
                    <Box sx={{ mb: 1, borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd' }}>
                      <img src={msg.image} alt="User upload" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', display: 'block' }} />
                    </Box>
                  )}
                  {msg.data && <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.data}</ReactMarkdown>}
                </Box>
              )}
              {msg.type === 'voice_prompt' && <ListItemText primaryTypographyProps={{ fontStyle: 'italic' }} primary={msg.data} />}

              {msg.type === 'order_proposal' && (
                <Box>
                  <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>Here's what I found. Does this look right?</Typography>
                  {msg.data.items.map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 1, borderLeft: '3px solid', borderColor: 'primary.main', mb: 1, gap: 1 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">
                          <strong>{item.quantity} {item.unit === 'weight' ? 'lb' : item.unit}</strong> of <strong>{item.product_name}</strong>
                        </Typography>
                        {item.is_special && (
                          <Typography variant="caption" component="div" color="text.secondary">
                            Special request item
                          </Typography>
                        )}
                      </Box>
                      <IconButton size="small" color="error" onClick={() => handleDeleteProposalItem(index, i)} sx={{ p: 0.5 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  {msg.data.delivery_date && (
                    <Typography variant="body2" sx={{ mt: 1.5, pl: 1, borderLeft: '3px solid', borderColor: 'secondary.main', mb: 1 }}>
                      Delivery Date: <strong>{msg.data.delivery_date}</strong>
                    </Typography>
                  )}
                  {msg.data.special_request_disclaimer && (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      {msg.data.special_request_disclaimer}
                    </Alert>
                  )}
                  <Button variant="contained" size="small" onClick={() => handleAddAiOrderToCart(msg.data)} startIcon={<ShoppingCartIcon />} sx={{ mt: 1.5, width: '100%', bgcolor: '#75a84b' }}>Proceed to Cart</Button>
                </Box>
              )}

              {msg.type === 'navigation_proposal' && (<Box><Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>{msg.data.prompt_text || "Ready to go?"}</Typography><Button variant="contained" size="small" onClick={() => handleNavigationClick(msg.data.path)} sx={{ width: '100%', bgcolor: '#75a84b', '&:hover': { bgcolor: '#7a8a54' } }}>{msg.data.button_text}</Button></Box>)}

              {msg.type === 'cancellation_proposal' && (
                <Box>
                  <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>
                    {msg.data.prompt_text}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button variant="contained" color="error" size="small" onClick={() => handleConfirmCancellation(msg.data.order_id)}>Yes, Cancel</Button>
                    <Button variant="outlined" size="small" onClick={() => setChatMessages(prev => [...prev, { sender: 'ai', type: 'text', data: 'Okay, I will not cancel the order.' }])}>No, Keep It</Button>
                  </Box>
                </Box>
              )}

            </Paper></ListItem>))}
            {isAiThinking && (
              <ListItem sx={{ justifyContent: 'flex-start' }}>
                <CircularProgress size={24} sx={{ mr: 1, color: '#75a84b' }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontStyle: 'italic',
                    animation: 'fadeIn 0.5s ease-in',
                    '@keyframes fadeIn': {
                      '0%': { opacity: 0 },
                      '100%': { opacity: 1 }
                    }
                  }}
                >
                  {loadingMessage}
                </Typography>
              </ListItem>
            )}
            <div ref={chatEndRef} />
          </List>
        </DialogContent>
        {chatSuggestions.length > 0 && (
          <Box sx={{ px: 2, pt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {chatSuggestions.map((suggestion, index) => (
              <Chip
                key={index}
                label={suggestion}
                onClick={() => handleSendChatMessage(suggestion)}
                sx={{
                  bgcolor: '#e6f2d9',
                  color: 'primary.main',
                  fontWeight: 500,
                  '&:hover': { bgcolor: '#dceec9', cursor: 'pointer' },
                  borderColor: 'primary.main',
                  border: '1px solid'
                }}
              />
            ))}
          </Box>
        )}
        <DialogActions sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center', position: 'relative' }}>
          {/* --- START OF EDIT: Conditional rendering for the tutorial --- */}
          {isChatOpen && showMicTutorial && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '120%',
                  right: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  color: 'white',
                  padding: '10px 15px',
                  borderRadius: '10px',
                  zIndex: 1400,
                  width: '240px',
                  pointerEvents: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                }}
              >
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Press this to speak your order.
                </Typography>
                <Typography variant="caption">
                  Press it again when you're done.
                </Typography>
              </Box>
              <WavyArrow />
            </>
          )}
          {/* --- END OF EDIT --- */}
          {/* Image Preview Area */}
          {selectedImage && (
            <Box sx={{ position: 'absolute', bottom: '100%', left: 0, right: 0, p: 2, bgcolor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(5px)', borderTop: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 2, zIndex: 1 }}>
              <Box sx={{ position: 'relative', height: 60, width: 60, borderRadius: 1, overflow: 'hidden', border: '1px solid #ccc' }}>
                <img src={URL.createObjectURL(selectedImage)} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
              <Typography variant="body2" sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedImage.name}
              </Typography>
              <IconButton size="small" onClick={handleClearImage}>
                <CloseIcon />
              </IconButton>
            </Box>
          )}

          <input
            type="file"
            accept="image/*"
            hidden
            ref={fileInputRef}
            onChange={handleFileSelect}
          />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            ref={cameraInputRef}
            onChange={handleFileSelect}
          />

          {isRecording ? (
            <AudioWaveform audioStream={audioStream} />
          ) : (
            <TextField fullWidth multiline maxRows={4} variant="outlined" placeholder="Type a message..." value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onPaste={handlePaste}
              onKeyPress={(e) => { if (!isMobile && e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChatMessage(); } }}
              disabled={isAiThinking}
              InputProps={{
                startAdornment: (
                  <>
                    {isMobile && (
                      <IconButton onClick={() => cameraInputRef.current?.click()} disabled={isAiThinking} sx={{ color: '#75a84b', mr: 0.5, p: 0.5 }}>
                        <CameraAltIcon />
                      </IconButton>
                    )}
                    <IconButton onClick={() => fileInputRef.current?.click()} disabled={isAiThinking} sx={{ color: '#75a84b', mr: 0.5, p: 0.5 }}>
                      <ImageIcon />
                    </IconButton>
                  </>
                ),
              }}
            />
          )}
          <Tooltip title={isRecording ? 'Stop Recording' : 'Record Voice Order'}>
            <span>
              <IconButton color={isRecording ? 'error' : 'primary'} onClick={handleToggleRecording} disabled={isAiThinking} sx={{ bgcolor: isRecording ? '#fee2e2' : '#75a84b', color: isRecording ? 'red' : 'white', '&:hover': { bgcolor: isRecording ? '#fecaca' : '#7a8a54' } }}>
                {isRecording ? <MicOffIcon /> : <MicIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton color="primary" onClick={() => handleSendChatMessage()} disabled={isAiThinking || isRecording || (!chatInput.trim() && !selectedImage)} sx={{ bgcolor: '#75a84b', color: 'white', '&:hover': { bgcolor: '#7a8a54' } }}><SendIcon /></IconButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <PageLoadingProvider>
          <AppContent />
        </PageLoadingProvider>
      </Router>
    </ThemeProvider>
  );
}
export default App;